/**
 * Portal Monitor API Service
 * Cookies are managed server-side via cookies.json — no need to send them from the frontend.
 */

import { API_ENDPOINTS, API_BASE, ADMIN_API_KEY } from '../config/api';

export interface PortalDocument {
  id: string;
  portal_url: string;
  title: string;
  section: string;
  doc_type: string | null;
  ticker: string | null;
  analyst: string | null;
  analyst_id: number | null;
  sector: string | null;
  pages: number | null;
  abstract: string | null;
  media_type: string | null;
  portal_date: string | null;
  discovered_at: string;
  status: string;
  ingestion_job_id: string | null;
  document_id: string | null;
  error_message: string | null;
  updated_at: string;
}

export interface MonitorStats {
  new: number;
  queued: number;
  ingesting: number;
  ingested: number;
  failed: number;
  skipped: number;
  by_section: Record<string, number>;
}

export interface ScanResult {
  new: number;
  existing: number;
  summary?: Record<string, any>;
}

export interface IngestResult {
  queued: number;
  errors: string[];
}

export interface DaemonStatus {
  status: 'running' | 'stopped' | 'error';
  is_alive: boolean;
  interval_minutes: number;
  auto_ingest: boolean;
  notifications_enabled: boolean;
  notification_channel: 'whatsapp' | 'telegram' | 'both';
  last_scan_at: string | null;
  last_scan_new: number;
  last_scan_existing: number;
  next_scan_eta: string | null;
  last_error: string | null;
  last_error_at: string | null;
  cookie_status: 'ok' | 'expired' | 'missing' | 'unknown';
  cookie_count: number;
  cookie_checked_at: string | null;
  heartbeat_at: string | null;
  heartbeat_age_sec: number | null;
  scan_count: number;
  started_at: string | null;
  pid: number | null;
}

export interface ScanHistoryEntry {
  id: number;
  scanned_at: string;
  new_count: number;
  existing_count: number;
  error: string | null;
  duration_sec: number | null;
}

// Keep backward compat alias
export type AutoScanStatus = DaemonStatus;

const headers = {
  'Content-Type': 'application/json',
  'X-API-Key': ADMIN_API_KEY,
};

async function request(url: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    ...options,
    headers: { ...headers, ...options.headers },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export const monitorService = {
  async getDocuments(params: {
    status?: string;
    section?: string;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ documents: PortalDocument[]; count: number }> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.section) qs.set('section', params.section);
    if (params.search) qs.set('search', params.search);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));

    const url = `${API_ENDPOINTS.monitorDocuments}?${qs.toString()}`;
    return request(url);
  },

  async getStats(): Promise<MonitorStats> {
    return request(API_ENDPOINTS.monitorStats);
  },

  async scanPortal(): Promise<ScanResult> {
    return request(API_ENDPOINTS.monitorScan, { method: 'POST' });
  },

  async ingestDocuments(documentIds: string[]): Promise<IngestResult> {
    return request(API_ENDPOINTS.monitorIngest, {
      method: 'POST',
      body: JSON.stringify({ document_ids: documentIds }),
    });
  },

  async updateDocument(id: string, updates: { status?: string }): Promise<void> {
    const url = `${API_ENDPOINTS.monitorDocuments}/${id}`;
    await request(url, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  // --- Daemon status & control ---

  async getDaemonStatus(): Promise<DaemonStatus> {
    return request(`${API_BASE}/monitor/auto-scan/status`);
  },

  // Backward compat
  async getAutoScanStatus(): Promise<DaemonStatus> {
    return this.getDaemonStatus();
  },

  async startAutoScan(): Promise<{ ok: boolean; message: string }> {
    return request(`${API_BASE}/monitor/auto-scan/start`, { method: 'POST' });
  },

  async stopAutoScan(): Promise<{ ok: boolean; message: string }> {
    return request(`${API_BASE}/monitor/auto-scan/stop`, { method: 'POST' });
  },

  async updateDaemonConfig(config: {
    interval_minutes?: number;
    auto_ingest?: boolean;
    notification_channel?: string;
  }): Promise<{ ok: boolean; updated: Record<string, any> }> {
    return request(`${API_BASE}/monitor/auto-scan/config`, {
      method: 'PATCH',
      body: JSON.stringify(config),
    });
  },

  async updateConfig(config: Record<string, any>): Promise<{ ok: boolean; updated: Record<string, any> }> {
    return request(`${API_BASE}/monitor/auto-scan/config`, {
      method: 'PATCH',
      body: JSON.stringify(config),
    });
  },

  async getScanHistory(limit: number = 20): Promise<{ scans: ScanHistoryEntry[] }> {
    return request(`${API_BASE}/monitor/scan-history?limit=${limit}`);
  },

  async reloadCookies(): Promise<{ ok: boolean; count: number }> {
    return request(`${API_BASE}/monitor/reload-cookies`, { method: 'POST' });
  },

  async enableNotifications(): Promise<{ ok: boolean; notifications_enabled: boolean }> {
    return request(`${API_BASE}/monitor/notifications/enable`, { method: 'POST' });
  },

  async disableNotifications(): Promise<{ ok: boolean; notifications_enabled: boolean }> {
    return request(`${API_BASE}/monitor/notifications/disable`, { method: 'POST' });
  },
};
